/**
 * Панель кнопок редактора описания (ТЗ 0066) — ЧИСТАЯ логика.
 *
 * Здесь нет ни DOM, ни React: функция получает текст с границами выделения и
 * возвращает новый текст с новыми границами. Поэтому поведение кнопок
 * проверяется тестом напрямую, а не «нажали и посмотрели»: у разметки много
 * краевых случаев (пустое выделение, несколько строк, повторное нажатие), и
 * ловить их через рендер дорого и ненадёжно.
 *
 * Формат — Markdown, а не HTML. Он и хранится как есть, и рисуется
 * `react-markdown` v6, который сырой HTML не выпускает вовсе: XSS в описании
 * не «отфильтрован», его физически неоткуда взять.
 */

/** Что умеет панель. Ключ — он же слаг локали (`pages.tasks.md.<key>`). */
export const MARKDOWN_ACTIONS = [
  'bold',
  'italic',
  'bullet',
  'ordered',
  'link',
  'code',
  'quote',
] as const;

export type TMarkdownAction = typeof MARKDOWN_ACTIONS[number];

export interface IMarkdownSelection {
  text: string;
  from: number;
  to: number;
}

/** Обёртки вокруг выделения: **жирный**, *курсив*, `код`. */
const WRAPPERS: Partial<Record<TMarkdownAction, string>> = {
  bold: '**',
  italic: '*',
  code: '`',
};

/** Префиксы строки: список, нумерация, цитата. */
const PREFIXES: Partial<Record<TMarkdownAction, (index: number) => string>> = {
  bullet: () => '- ',
  ordered: (index) => `${index + 1}. `,
  quote: () => '> ',
};

/** Границы строки, в которую попадает позиция. */
const lineBounds = (text: string, from: number, to: number) => {
  const start = text.lastIndexOf('\n', from - 1) + 1;
  const breakAt = text.indexOf('\n', to);
  return { start, end: breakAt === -1 ? text.length : breakAt };
};

const applyWrapper = (
  { text, from, to }: IMarkdownSelection,
  mark: string,
): IMarkdownSelection => {
  const selected = text.slice(from, to);
  const before = text.slice(0, from);
  const after = text.slice(to);

  // Повторное нажатие на уже размеченном куске СНИМАЕТ разметку. Кнопка,
  // которая умеет только добавлять, оставляет ****жирный**** после второго
  // клика — и человек лезет чистить это руками.
  if (
    selected.startsWith(mark) &&
    selected.endsWith(mark) &&
    selected.length >= mark.length * 2
  ) {
    const stripped = selected.slice(mark.length, selected.length - mark.length);
    return {
      text: before + stripped + after,
      from,
      to: from + stripped.length,
    };
  }
  if (before.endsWith(mark) && after.startsWith(mark)) {
    return {
      text: before.slice(0, -mark.length) + selected + after.slice(mark.length),
      from: from - mark.length,
      to: to - mark.length,
    };
  }

  // Пустое выделение — ставим пару и оставляем курсор ВНУТРИ: иначе человек
  // печатает за закрывающими звёздочками и получает не то, что просил.
  const body = selected || '';
  return {
    text: `${before}${mark}${body}${mark}${after}`,
    from: from + mark.length,
    to: from + mark.length + body.length,
  };
};

const applyPrefix = (
  { text, from, to }: IMarkdownSelection,
  make: (index: number) => string,
): IMarkdownSelection => {
  const { start, end } = lineBounds(text, from, to);
  const lines = text.slice(start, end).split('\n');
  // Уже размечено целиком — снимаем. Тот же принцип, что у обёрток.
  const marked = lines.every((line, index) => line.startsWith(make(index)));
  const next = lines
    .map((line, index) =>
      marked ? line.slice(make(index).length) : `${make(index)}${line}`,
    )
    .join('\n');
  return {
    text: text.slice(0, start) + next + text.slice(end),
    from: start,
    to: start + next.length,
  };
};

const applyLink = ({
  text,
  from,
  to,
}: IMarkdownSelection): IMarkdownSelection => {
  const selected = text.slice(from, to);
  const before = text.slice(0, from);
  const after = text.slice(to);
  // Выделили сам адрес — он и становится адресом, а курсор встаёт в подпись.
  const looksLikeUrl = /^(https?:\/\/|www\.|mailto:)/i.test(selected.trim());
  if (looksLikeUrl) {
    const url = selected.trim();
    return {
      text: `${before}[](${url})${after}`,
      from: from + 1,
      to: from + 1,
    };
  }
  const label = selected || '';
  return {
    text: `${before}[${label}](url)${after}`,
    // Курсор — на слове `url`, чтобы его сразу заменили вставкой.
    from: from + label.length + 3,
    to: from + label.length + 6,
  };
};

/**
 * Применить действие панели к выделению.
 *
 * Возвращает новый текст и новые границы выделения — вызывающий обязан
 * вернуть их в поле, иначе курсор прыгнет в конец и следующая кнопка
 * разметит не то место.
 */
export const applyMarkdown = (
  selection: IMarkdownSelection,
  action: TMarkdownAction,
): IMarkdownSelection => {
  const wrapper = WRAPPERS[action];
  if (wrapper) return applyWrapper(selection, wrapper);
  const prefix = PREFIXES[action];
  if (prefix) return applyPrefix(selection, prefix);
  return applyLink(selection);
};

/** Схемы, которым разрешено попадать в `href` описания. */
const SAFE_SCHEMES = ['http:', 'https:', 'mailto:', 'tel:'];

/**
 * Адрес ссылки для показа описания — или пустая строка.
 *
 * Своя проверка вместо встроенной в react-markdown: та подменяет запрещённую
 * схему на `javascript:void(0)`, то есть javascript-адрес всё равно остаётся
 * в разметке (React на это отдельно ругается в консоли). Пустой `href` честнее
 * и не требует объяснять читателю кода, почему «javascript: — это нормально».
 *
 * Относительные адреса пропускаем: якорь и путь внутри ERP безопасны.
 */
export const safeHref = (raw?: string): string => {
  const value = (raw || '').trim();
  if (!value) return '';
  const scheme = value.match(/^[a-zA-Z][a-zA-Z0-9+.-]*:/);
  if (!scheme) return value;
  return SAFE_SCHEMES.includes(scheme[0].toLowerCase()) ? value : '';
};
